import localforage from 'localforage';

// Keys for storing different data types
const CACHE_KEYS = {
  ITEMS: 'cache_items',
  ITEMS_TIMESTAMP: 'cache_items_timestamp',
  BATCHES: 'cache_batches',
  BATCHES_TIMESTAMP: 'cache_batches_timestamp',
  RECEIPTS: 'cache_receipts',
  RECEIPTS_TIMESTAMP: 'cache_receipts_timestamp',
  CATEGORIES: 'cache_categories',
  CATEGORIES_TIMESTAMP: 'cache_categories_timestamp',
  BRANDS: 'cache_brands',
  BRANDS_TIMESTAMP: 'cache_brands_timestamp',
  LOW_STOCK: 'cache_low_stock',
  LOW_STOCK_TIMESTAMP: 'cache_low_stock_timestamp',
  DASHBOARD_STATS: 'cache_dashboard_stats',
  DASHBOARD_STATS_TIMESTAMP: 'cache_dashboard_stats_timestamp',
};

// Cache duration in milliseconds (1 hour)
const CACHE_DURATION = 60 * 60 * 1000;

/**
 * Save data to offline cache
 */
export const saveToCache = async (key, data) => {
  try {
    const cacheKey = CACHE_KEYS[key];
    const timestampKey = `${key}_TIMESTAMP`;
    
    if (!cacheKey) {
      console.warn(`[Cache] Unknown cache key: ${key}`);
      return;
    }

    await localforage.setItem(cacheKey, data);
    await localforage.setItem(CACHE_KEYS[timestampKey], Date.now());
    console.log(`[Cache] ✓ Saved ${key} (${data?.length || 1} items)`);
  } catch (error) {
    console.error(`[Cache] ✗ Error saving ${key}:`, error);
  }
};

/**
 * Get data from offline cache
 */
export const getFromCache = async (key) => {
  try {
    const cacheKey = CACHE_KEYS[key];
    const timestampKey = `${key}_TIMESTAMP`;
    
    if (!cacheKey) {
      console.warn(`[Cache] Unknown cache key: ${key}`);
      return null;
    }

    const data = await localforage.getItem(cacheKey);
    const timestamp = await localforage.getItem(CACHE_KEYS[timestampKey]);

    if (!data) {
      console.log(`[Cache] No cached data for ${key}`);
      return null;
    }

    // Check if cache is expired (optional - for now we keep data indefinitely)
    if (timestamp) {
      const age = Date.now() - timestamp;
      const ageInMinutes = Math.floor(age / (1000 * 60));
      console.log(`[Cache] ✓ Retrieved ${key} (age: ${ageInMinutes} minutes)`);
    }

    return data;
  } catch (error) {
    console.error(`[Cache] ✗ Error retrieving ${key}:`, error);
    return null;
  }
};

/**
 * Clear specific cache or all caches
 */
export const clearCache = async (key = null) => {
  try {
    if (key) {
      const cacheKey = CACHE_KEYS[key];
      const timestampKey = `${key}_TIMESTAMP`;
      
      if (cacheKey) {
        await localforage.removeItem(cacheKey);
        await localforage.removeItem(CACHE_KEYS[timestampKey]);
        console.log(`[Cache] ✓ Cleared ${key}`);
      }
    } else {
      // Clear all caches
      const allKeys = Object.values(CACHE_KEYS);
      await Promise.all(allKeys.map(k => localforage.removeItem(k)));
      console.log(`[Cache] ✓ Cleared all caches`);
    }
  } catch (error) {
    console.error(`[Cache] ✗ Error clearing cache:`, error);
  }
};

/**
 * Get cache status/info
 */
export const getCacheInfo = async () => {
  try {
    const info = {};
    
    for (const [key, cacheKey] of Object.entries(CACHE_KEYS)) {
      if (key.includes('_TIMESTAMP')) continue; // Skip timestamp keys
      
      const data = await localforage.getItem(cacheKey);
      const timestamp = await localforage.getItem(CACHE_KEYS[`${key}_TIMESTAMP`]);
      
      info[key] = {
        exists: !!data,
        itemCount: Array.isArray(data) ? data.length : (data ? 1 : 0),
        timestamp: timestamp,
        age: timestamp ? Date.now() - timestamp : null
      };
    }
    
    return info;
  } catch (error) {
    console.error(`[Cache] ✗ Error getting cache info:`, error);
    return {};
  }
};

/**
 * Log all cache information to console
 */
export const debugCache = async () => {
  console.log('[Cache] ========== CACHE DEBUG INFO ==========');
  const info = await getCacheInfo();
  
  Object.entries(info).forEach(([key, data]) => {
    if (data.exists) {
      const ageMinutes = data.age ? Math.floor(data.age / (1000 * 60)) : 'unknown';
      console.log(`  ${key}: ${data.itemCount} items (age: ${ageMinutes} min)`);
    }
  });
  
  console.log('[Cache] ========================================');
};

export default {
  saveToCache,
  getFromCache,
  clearCache,
  getCacheInfo,
  debugCache,
  CACHE_KEYS,
};
